import type { TeamReferenceStory } from '@rhythmic/db/types'
import type { UpdateTeamReferenceStoryPayload } from '@rhythmic/types'
import { UpdateTeamReferenceStorySchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getTeamReferenceStoryCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:updateReferenceStory')

export interface UpdateReferenceStoryContext {
  teamId: string
}

export interface UpdateReferenceStoryParams {
  id: string
  changes: UpdateTeamReferenceStoryPayload
}

function validateParams(params: UpdateReferenceStoryParams, context: UpdateReferenceStoryContext) {
  const { id, changes } = params
  const { teamId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to update reference story: missing team identifier')
    throw error
  }

  if (!id) {
    const error = new Error('Reference story identifier is required to update')
    logger.error('Unable to update reference story: missing story identifier')
    throw error
  }

  const parsedChanges = UpdateTeamReferenceStorySchema.parse(changes)

  const applicable = Object.fromEntries(
    Object.entries(parsedChanges).filter(([, value]) => value !== undefined),
  ) as Partial<UpdateTeamReferenceStoryPayload>

  if (Object.keys(applicable).length === 0) {
    const error = new Error('No changes provided to update reference story')
    logger.warn('Reference story update aborted: no changes detected', {
      teamId,
      id,
    })
    throw error
  }

  return {
    teamId,
    id,
    changes: applicable,
  }
}

export default function handleUpdateReferenceStory(
  params: UpdateReferenceStoryParams,
  context: UpdateReferenceStoryContext,
): Transaction {
  try {
    const { teamId, id, changes } = validateParams(params, context)
    const collection = getTeamReferenceStoryCollection(teamId)

    const result = collection.update(id, (draft: TeamReferenceStory) => {
      if (changes.story !== undefined) draft.story = changes.story
      if (changes.estimate !== undefined) draft.estimate = changes.estimate
      if (changes.description !== undefined) draft.description = changes.description
      draft.updated_at = new Date()
    })

    logger.info('Reference story updated via collection', { teamId, id })

    return result
  } catch (error) {
    logger.error('updateReferenceStory failed', { params, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to update reference story')
  }
}
